import React, { useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  Download, Upload, X, FileSpreadsheet, AlertTriangle,
  CheckCircle2, FileWarning, Loader2,
} from "lucide-react";
import * as XLSX from "xlsx";
import apiClient from "../../utils/apiClient";
import { AppButton, AppBadge } from "../ui";
import "./AIQuestionsImportModal.scss";

const VALID_DIFFICULTIES = ["easy", "medium", "hard"];

// Sample rows shown in the downloadable template — covers multiple tech
// stacks and all difficulty levels so customers see the expected format.
const TEMPLATE_ROWS = [
  { question: "What is a closure in JavaScript? Give a real-world example.", techStack: "JavaScript", difficulty: "easy" },
  { question: "Explain the difference between let, const, and var.", techStack: "JavaScript", difficulty: "easy" },
  { question: "How does the event loop work in Node.js?", techStack: "JavaScript", difficulty: "medium" },
  { question: "Describe how you would optimize a React app that re-renders too often.", techStack: "React", difficulty: "medium" },
  { question: "Compare useMemo and useCallback. When would you avoid them?", techStack: "React", difficulty: "hard" },
  { question: "What is the JVM memory model and how does garbage collection work?", techStack: "Java", difficulty: "medium" },
  { question: "Design a thread-safe LRU cache in Java.", techStack: "Java", difficulty: "hard" },
  { question: "What is the difference between INNER JOIN and LEFT JOIN in SQL?", techStack: "SQL", difficulty: "easy" },
  { question: "Explain database indexing and when it can hurt performance.", techStack: "SQL", difficulty: "medium" },
  { question: "Design a URL shortener like bit.ly. Discuss trade-offs.", techStack: "System Design", difficulty: "hard" },
];

const TEMPLATE_INSTRUCTIONS = [
  ["AI Questions Import — Instructions"],
  [],
  ["1. Use the 'Questions' sheet to add your questions. Do not rename columns."],
  ["2. Required columns:"],
  ["   • question      The interview question text (required)"],
  ["   • techStack     The category / tech stack (e.g. JavaScript, Java, React)"],
  ["   • difficulty    One of: easy, medium, hard (lower-case)"],
  [],
  ["3. Tips:"],
  ["   • Each row = one question."],
  ["   • Keep questions clear and self-contained — students will see them as-is."],
  ["   • Tech stacks you don't have yet will be created automatically."],
  ["   • Rows with missing question / techStack / invalid difficulty are skipped."],
  [],
  ["4. Supported file formats: .xlsx, .xls, .csv"],
  [],
  ["Allowed difficulty values:", "easy", "medium", "hard"],
];

const AIQuestionsImportModal = ({ show, onHide, onImported }) => {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFileName("");
    setParsedRows([]);
    setParseError("");
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (importing) return;
    reset();
    onHide();
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1 — Questions (the actual data)
    const wsQuestions = XLSX.utils.json_to_sheet(TEMPLATE_ROWS, {
      header: ["question", "techStack", "difficulty"],
    });
    wsQuestions["!cols"] = [
      { wch: 70 }, // question
      { wch: 18 }, // techStack
      { wch: 12 }, // difficulty
    ];
    XLSX.utils.book_append_sheet(wb, wsQuestions, "Questions");

    // Sheet 2 — Instructions
    const wsInstructions = XLSX.utils.aoa_to_sheet(TEMPLATE_INSTRUCTIONS);
    wsInstructions["!cols"] = [{ wch: 60 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, wsInstructions, "Instructions");

    XLSX.writeFile(wb, "ai-questions-sample.xlsx");
  };

  const handleFileChange = async (e) => {
    setParseError("");
    setResult(null);
    setParsedRows([]);
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheetName = wb.SheetNames[0];
      if (!sheetName) {
        setParseError("File contains no sheets.");
        return;
      }
      const sheet = wb.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!Array.isArray(rows) || rows.length === 0) {
        setParseError("No rows found in the first sheet.");
        return;
      }
      const normalized = rows.map((r) => {
        const out = { question: "", techStack: "", difficulty: "" };
        for (const key of Object.keys(r)) {
          const k = key.trim().toLowerCase();
          const v = (r[key] ?? "").toString().trim();
          if (k === "question") out.question = v;
          else if (k === "techstack" || k === "tech stack" || k === "category") out.techStack = v;
          else if (k === "difficulty" || k === "level") out.difficulty = v.toLowerCase();
        }
        return out;
      });
      setParsedRows(normalized);
    } catch (err) {
      console.error(err);
      setParseError("Failed to parse the file. Use the provided template (.xlsx or .csv).");
    }
  };

  const previewIssues = parsedRows.reduce(
    (acc, r) => {
      if (!r.question) acc.missingQuestion += 1;
      if (!r.techStack) acc.missingTechStack += 1;
      if (!VALID_DIFFICULTIES.includes(r.difficulty)) acc.invalidDifficulty += 1;
      return acc;
    },
    { missingQuestion: 0, missingTechStack: 0, invalidDifficulty: 0 }
  );

  const validRowCount = parsedRows.filter(
    (r) => r.question && r.techStack && VALID_DIFFICULTIES.includes(r.difficulty)
  ).length;

  const handleImport = async () => {
    if (parsedRows.length === 0) return;
    setImporting(true);
    setResult(null);
    try {
      const { data } = await apiClient.post("/ai/ai-questions/import", {
        questions: parsedRows,
      });
      setResult({ ok: true, ...data });
      if (data?.inserted > 0 && typeof onImported === "function") {
        onImported();
      }
    } catch (err) {
      setResult({ ok: false, message: err?.response?.data?.message || "Import failed." });
    }
    setImporting(false);
  };

  const triggerFilePicker = () => fileInputRef.current?.click();

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop={importing ? "static" : true}
      className="aiq-import-modal"
      size="lg"
    >
      <div className="aiq-import-modal__header">
        <div className="aiq-import-modal__title">
          <div className="aiq-import-modal__icon">
            <Upload size={18} />
          </div>
          <div>
            <h2>Import AI Questions</h2>
            <p>Bulk-add questions from an Excel or CSV file.</p>
          </div>
        </div>
        <button
          type="button"
          className="aiq-import-modal__close"
          onClick={handleClose}
          disabled={importing}
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="aiq-import-modal__body">
        {/* Template / instructions card */}
        <div className="aiq-import-card">
          <div className="aiq-import-card__head">
            <div className="aiq-import-card__head-icon"><FileSpreadsheet size={16} /></div>
            <div>
              <h3>File format</h3>
              <p>
                Required columns: <code>question</code>, <code>techStack</code>,{" "}
                <code>difficulty</code> (easy / medium / hard). Download the sample
                file to see ready-to-edit examples.
              </p>
            </div>
            <AppButton variant="outline" size="sm" onClick={handleDownloadTemplate}>
              <Download size={14} /> <span>Sample File</span>
            </AppButton>
          </div>
        </div>

        {/* Dropzone-style file picker */}
        <button
          type="button"
          className={`aiq-import-drop${fileName ? " aiq-import-drop--has-file" : ""}`}
          onClick={triggerFilePicker}
          disabled={importing}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            disabled={importing}
            hidden
          />
          <div className="aiq-import-drop__icon">
            <Upload size={22} />
          </div>
          <div className="aiq-import-drop__text">
            {fileName ? (
              <>
                <div className="aiq-import-drop__filename">{fileName}</div>
                <div className="aiq-import-drop__hint">Click to replace</div>
              </>
            ) : (
              <>
                <div className="aiq-import-drop__filename">Choose a file to import</div>
                <div className="aiq-import-drop__hint">.xlsx, .xls, or .csv</div>
              </>
            )}
          </div>
        </button>

        {parseError && (
          <div className="aiq-import-callout aiq-import-callout--danger">
            <AlertTriangle size={16} />
            <div>{parseError}</div>
          </div>
        )}

        {parsedRows.length > 0 && !result && (
          <div className="aiq-import-summary">
            <div className="aiq-import-summary__row">
              <span className="aiq-import-summary__label">Detected</span>
              <AppBadge color="blue" size="sm">{parsedRows.length} row(s)</AppBadge>
            </div>
            <div className="aiq-import-summary__row">
              <span className="aiq-import-summary__label">Valid</span>
              <AppBadge color="emerald" size="sm">{validRowCount}</AppBadge>
            </div>
            {(previewIssues.missingQuestion > 0 ||
              previewIssues.missingTechStack > 0 ||
              previewIssues.invalidDifficulty > 0) && (
              <div className="aiq-import-callout aiq-import-callout--warning">
                <FileWarning size={16} />
                <div>
                  <div className="aiq-import-callout__title">Some rows will be skipped</div>
                  <ul>
                    {previewIssues.missingQuestion > 0 && (
                      <li>{previewIssues.missingQuestion} missing <code>question</code></li>
                    )}
                    {previewIssues.missingTechStack > 0 && (
                      <li>{previewIssues.missingTechStack} missing <code>techStack</code></li>
                    )}
                    {previewIssues.invalidDifficulty > 0 && (
                      <li>{previewIssues.invalidDifficulty} with invalid <code>difficulty</code></li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {result && result.ok && (
          <div className="aiq-import-callout aiq-import-callout--success">
            <CheckCircle2 size={16} />
            <div>
              <div className="aiq-import-callout__title">
                Imported {result.inserted} of {result.total}
              </div>
              {result.skipped > 0 && (
                <div className="aiq-import-callout__sub">
                  Skipped {result.skipped} invalid row(s).
                </div>
              )}
              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <details className="aiq-import-errors">
                  <summary>View errors ({result.errors.length})</summary>
                  <ul>
                    {result.errors.map((e, i) => (
                      <li key={i}>Row {e.row}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </div>
        )}

        {result && !result.ok && (
          <div className="aiq-import-callout aiq-import-callout--danger">
            <AlertTriangle size={16} />
            <div>{result.message}</div>
          </div>
        )}
      </div>

      <div className="aiq-import-modal__footer">
        <AppButton variant="ghost" onClick={handleClose} disabled={importing}>
          {result?.ok ? "Close" : "Cancel"}
        </AppButton>
        <AppButton
          variant="default"
          onClick={handleImport}
          disabled={importing || parsedRows.length === 0 || !!result?.ok}
        >
          {importing ? (
            <>
              <Loader2 size={14} className="aiq-import-modal__spin" />
              <span>Importing...</span>
            </>
          ) : (
            <>
              <Upload size={14} />
              <span>Import{validRowCount ? ` ${validRowCount}` : ""}</span>
            </>
          )}
        </AppButton>
      </div>
    </Modal>
  );
};

export default AIQuestionsImportModal;
